import React, { memo, useEffect, useRef, useState, forwardRef } from "react";
import { Grid, styled, Theme, useTheme } from "@mui/material";
import { ShortCodeChip } from "src/components/ShortCodes/ShortCodeChip";
import ListItem from "@mui/material/ListItem";
import { FixedSizeList as List, areEqual } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import memoize from "memoize-one";
import { oneDayMillis } from "src/pages/CrewSchedule/gl/WaterfallGL";
import { IPairingPosition } from "@vmosolutions/oc-interfaces";
import { PairingPositionCell } from "src/components/FilterSearchTable/CustomCellComponents/PairingPositionCell";

export interface IRowItem {
  label: string;
  _id: string;
  isSelected: boolean;
  isExpanded: boolean;
  lastModified: number;
  startDate: number;
  endDate: number;
  positions: IPairingPosition[];
}

interface IPairingWaterfallListProps {
  id: string;
  itemList: IRowItem[];
  setFocusOnDate?: (date?: Date, toFront?: boolean) => void;
  setCurrentRange: any;
}

const OCPairingWaterfallList = styled(Grid)(({ theme }) => ({
  display: "flex",
  width: "100%",
  flexGrow: 1,
  border: 2,
  flexDirection: "column",
  overflow: "hidden",
  flexBasis: 0,
  position: "relative",
  flexWrap: "nowrap",
  borderRight: "4px solid " + theme.custom.timeline.groupColumnDivider,
  "& li.item_details": {
    width: "100%",
    padding: 0,
    "&:hover:not(.presentation)": {
      backgroundImage: `linear-gradient(to right, ${theme.palette.background.paper}, ${theme.custom.timeline.contextListBackground})`,
      "& .pairing": {
        backgroundColor: "#ffffff",
        "& svg path": { fill: "#ffffff" },
      },
    },
  },
}));

// If list items are expensive to render,
// Consider using React.memo or shouldComponentUpdate to avoid unnecessary re-renders.
// https://reactjs.org/docs/react-api.html#reactmemo
// https://reactjs.org/docs/react-api.html#reactpurecomponent
const RowItem = memo(({ data, index, style }: any) => {
  const { items, setFocusOnDate, setCurrentRange } = data;
  const item: IRowItem = items[index];
  return (
    <ListItem key={item?._id} className="item_details" style={style}>
      <Grid container spacing={0}>
        <Grid item wrap="nowrap" alignItems="center" xs={"auto"} sx={{ display: "flex" }}>
          <ShortCodeChip
            code={item.label ?? `P${index + 1}`}
            itemId={item._id}
            startDate={item.startDate}
            endDate={item.endDate}
            positions={item.positions}
            type="pairing"
            onClickChip={() => {
              setCurrentRange([item.startDate - oneDayMillis / 2, item.startDate + oneDayMillis * 30]);
              setFocusOnDate(new Date(item.startDate), true);
            }}
          />
          {Array.isArray(item?.positions) && item.positions.length > 0 && (
            <Grid item xs>
              <PairingPositionCell positions={item.positions} />
            </Grid>
          )}
        </Grid>
      </Grid>
    </ListItem>
  );
}, areEqual);

/**
 * Pairing Waterfall list will display all pairings loaded into waterfall as a vertical list of shortcode chips
 * These chips will be interactive (context menu) as well as draggable (into crew sched) so pairings can be assigned crew members.
 * @param props -
 * @returns
 */
export const PairingWaterfallList = forwardRef((props: IPairingWaterfallListProps, ref: any): JSX.Element => {
  const { id, itemList, setFocusOnDate, setCurrentRange } = props;
  const [listRef, setListRef] = useState(null);
  const theme: Theme = useTheme();

  // This helper function memoizes incoming props,
  // To avoid causing unnecessary re-renders pure Row components.
  // This is only needed since we are passing multiple props with a wrapper object.
  // If we were only passing a single, stable value (e.g. items),
  // We could just pass the value directly.
  const createItemData = memoize((itemList) => ({ items: itemList, setFocusOnDate, setCurrentRange }));

  // Bundle additional data to list items using the "itemData" prop.
  // It will be accessible to item renderers as props.data.
  // Memoize this data to avoid bypassing shouldComponentUpdate().
  const itemData = createItemData(itemList);

  return (
    <OCPairingWaterfallList container direction="column">
      <AutoSizer>
        {({ height, width }: any) => (
          <List
            ref={ref}
            height={height}
            width={width}
            itemSize={36}
            itemCount={itemList?.length}
            itemData={itemData}
            style={{ overflow: "hidden", whiteSpace: "nowrap" }}
          >
            {RowItem}
          </List>
        )}
      </AutoSizer>
    </OCPairingWaterfallList>
  );
});
